/**
 * stepTypeMapping.ts - Utilities for mapping between node types and API step types
 *
 * Provides conversion between the UI node types and the API AutomationStepType enum,
 * as well as default step data for creating new steps.
 *
 * Related:
 *   - types/nodes.ts - NODE_TYPES constants
 *   - utils/nodeFactory.ts - Step node creation
 */

import * as Api from '@ViewModels';
import { NODE_TYPES } from '../types';
import { normalizeBehaviorBranchSchedule } from './behaviorBranchSchedule';
import { normalizeTriggerAutomationStepSchedule } from './triggerAutomationStepRules';

export const TRIGGER_AUTOMATION_STEP_TYPE = 'TriggerAutomation';

export type AutomationCanvasStepType = Api.AutomationStepType | typeof TRIGGER_AUTOMATION_STEP_TYPE;

/**
 * Maps UI node types to API AutomationStepType enum values.
 */
export const NODE_TYPE_TO_STEP_TYPE: Record<string, AutomationCanvasStepType | null> = {
	[NODE_TYPES.EMAIL_STEP]: Api.AutomationStepType.Email,
	[NODE_TYPES.TEXTING_STEP]: Api.AutomationStepType.Texting,
	[NODE_TYPES.ACTION_ITEM_STEP]: Api.AutomationStepType.ActionItem,
	[NODE_TYPES.ADD_TAG_STEP]: Api.AutomationStepType.AddTag,
	[NODE_TYPES.REMOVE_TAG_STEP]: Api.AutomationStepType.RemoveTag,
	[NODE_TYPES.HANDWRITTEN_CARD_STEP]: Api.AutomationStepType.HandwrittenCard,
	[NODE_TYPES.NO_ACTION_STEP]: Api.AutomationStepType.NoAction,
	[NODE_TYPES.OPPORTUNITY_STEP]: Api.AutomationStepType.Opportunity,
	[NODE_TYPES.HTML_NEWSLETTER_STEP]: Api.AutomationStepType.HtmlNewsletter,
	[NODE_TYPES.SWITCH_STEP]: Api.AutomationStepType.Switch,
	[NODE_TYPES.TRIGGER_AUTOMATION_STEP]: TRIGGER_AUTOMATION_STEP_TYPE,
	// Trigger is not a step type
	[NODE_TYPES.TRIGGER]: null,
};

/**
 * Converts a UI node type to the corresponding API step type.
 *
 * @param nodeType - The UI node type string
 * @returns The API AutomationStepType or null if not a step type
 */
export function nodeTypeToStepType(nodeType: string): AutomationCanvasStepType | null {
	return NODE_TYPE_TO_STEP_TYPE[nodeType] ?? null;
}

/**
 * Creates a default step object for a given step type.
 * This provides minimal required data for creating a new step via the API.
 *
 * @param stepType - The API AutomationStepType
 * @param trigger - Optional automation trigger to derive default schedule
 * @returns A minimal step object suitable for API creation
 */
export function createDefaultStepData(
	stepType: AutomationCanvasStepType,
	trigger?: Api.IAutomationTrigger,
	options: { normalizeForBehaviorBranch?: boolean } = {}
): Record<string, unknown> {
	// Get default schedule based on step type and trigger
	const scheduleStepType = stepType === TRIGGER_AUTOMATION_STEP_TYPE ? Api.AutomationStepType.Opportunity : stepType;
	const defaultSchedule = Api.VmUtils.Automations.steps.getDefaultScheduleForAutomationStep(scheduleStepType, trigger);
	const schedule =
		stepType === TRIGGER_AUTOMATION_STEP_TYPE
			? normalizeTriggerAutomationStepSchedule(
					defaultSchedule,
					options.normalizeForBehaviorBranch ? trigger : undefined,
					{
						normalizeToRootTrigger: options.normalizeForBehaviorBranch,
					}
				)
			: options.normalizeForBehaviorBranch
				? normalizeBehaviorBranchSchedule(defaultSchedule, trigger, stepType)
				: defaultSchedule;
	const name = getDefaultStepName(stepType);

	// Return type-specific step data
	switch (stepType) {
		case Api.AutomationStepType.Email:
			return {
				name,
				schedule,
				subject: '',
				content: '',
				options: {},
			};

		case Api.AutomationStepType.Texting:
			return {
				name,
				schedule,
				content: '',
			};

		case Api.AutomationStepType.ActionItem:
			return {
				name,
				schedule,
			};

		case Api.AutomationStepType.AddTag:
			return {
				name,
				schedule,
				tagName: '',
			};

		case Api.AutomationStepType.RemoveTag:
			return {
				name,
				schedule,
				tagName: '',
			};

		case Api.AutomationStepType.HandwrittenCard:
			return {
				name,
				schedule,
			};

		case Api.AutomationStepType.NoAction:
			return {
				name: 'Do nothing',
				schedule: { criteria: Api.AutomationStepScheduleCriteria.Immediately },
			};

		case Api.AutomationStepType.Opportunity:
			return {
				name,
				schedule,
			};

		case Api.AutomationStepType.HtmlNewsletter:
			return {
				name,
				schedule,
				subject: '',
				content: '',
				options: {},
			};

		case Api.AutomationStepType.Switch:
			return {
				name,
				schedule,
				cases: [
					{
						isDefault: true,
						name: 'Default',
					},
				],
			};

		default:
			if (stepType === TRIGGER_AUTOMATION_STEP_TYPE) {
				return {
					name,
					schedule,
					targetAutomationTemplateId: '',
					targetAutomationTemplateName: '',
				};
			}
			return {
				name,
				schedule,
			};
	}
}

/**
 * Gets a default name for a step type.
 */
function getDefaultStepName(stepType: AutomationCanvasStepType): string {
	switch (stepType) {
		case Api.AutomationStepType.Email:
			return 'New Email';
		case Api.AutomationStepType.Texting:
			return 'New Text Message';
		case Api.AutomationStepType.ActionItem:
			return 'New Action Item';
		case Api.AutomationStepType.AddTag:
			return 'Add Tag';
		case Api.AutomationStepType.RemoveTag:
			return 'Remove Tag';
		case Api.AutomationStepType.HandwrittenCard:
			return 'New Handwritten Card';
		case Api.AutomationStepType.NoAction:
			return 'Do nothing';
		case Api.AutomationStepType.Opportunity:
			return 'New Opportunity';
		case Api.AutomationStepType.HtmlNewsletter:
			return 'New Newsletter';
		case Api.AutomationStepType.Switch:
			return 'Conditional Branch';
		default:
			if (stepType === TRIGGER_AUTOMATION_STEP_TYPE) {
				return 'Start Automation';
			}
			return 'New Step';
	}
}
